import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Subscription } from 'rxjs';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';
import { LimeResourceItem } from './interfaces/lime-resource-item';
import { useService } from '../../../services/services';
import DashboardLoading from '../../dashboard/components/dashboard-loading';
import StaticTable, {
  StaticTableColumn,
} from '../../../components/common/table/static-table';
import { useNotes } from './components/use-notes';
import { SchemaContext, StepProps } from './create';
import css from './index.module.css';

export default function StepLimeResources({
  mode,
  setIsChildLoading,
}: StepProps) {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.vaad_liz_reports_v2.lime_resources.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId, id } = useParams<{ farmId: string; id: string }>();
  const { vaadLizReportV2Service: vaadLizReportService } = useService();
  const [tableData, setTableData] = useState<{
    rows: LimeResourceItem[];
  } | null>(null);
  const { getNotesElement, modal } = useNotes('lime_resources', mode);
  const { setSchema } = useContext(SchemaContext);
  const form = useFormContext();
  const { setValue } = form;

  const createSchema = useCallback(() => {
    return yup.object().shape({});
  }, []);

  const sortLimeResourceItems = useCallback(
    (a: LimeResourceItem, b: LimeResourceItem) => {
      const nameComparison = a.product_name.localeCompare(b.product_name, 'lv');
      if (nameComparison !== 0) return nameComparison;

      return a.id.localeCompare(b.id);
    },
    []
  );

  useEffect(() => {
    setIsChildLoading(true);

    const subscription: Subscription = vaadLizReportService
      .getLimeResources(farmId, id)
      .subscribe(
        (data: LimeResourceItem[]) => {
          data.sort(sortLimeResourceItems);

          const formValues = { lime_resources: {} };
          const tableData: LimeResourceItem[] = data.map(
            (row: LimeResourceItem) => {
              const name = `lime_resources.${row.id}`;
              formValues.lime_resources[row.id] = { ...row };

              const tableRow = { ...row };
              tableRow.notes_edit = getNotesElement(name, tableRow);
              return tableRow;
            }
          );

          setSchema(createSchema());
          setTableData({ rows: tableData });
          setIsChildLoading(false);
          setValue('lime_resources', formValues.lime_resources);
        },
        (error) => {
          console.error('Error fetching data', error);
          setIsChildLoading(false);
        }
      );

    return () => subscription.unsubscribe();
  }, [
    vaadLizReportService,
    farmId,
    id,
    setValue,
    getNotesElement,
    setIsChildLoading,
    sortLimeResourceItems,
    setSchema,
    createSchema,
  ]);

  const columns = useCallback(() => {
    const baseColumns: StaticTableColumn<LimeResourceItem>[] = [
      {
        accessor: 'product_type',
        header: tp('product_type'),
        className: css['strong'],
      },
      { accessor: 'product_name', header: tp('product_name') },
      { accessor: 'quantity', header: tp('quantity') },
      { accessor: 'unit', header: tp('unit') },
      { accessor: 'notes_edit', header: tp('notes') },
    ];

    return baseColumns;
  }, [tp]);

  return !tableData ? (
    <DashboardLoading />
  ) : (
    <div className={`${css['table']} ${css['lime-table']}`}>
      <StaticTable<LimeResourceItem>
        data={tableData}
        keyAccessor="id"
        columns={columns()}
        fixedHeight
      />

      {modal}
    </div>
  );
}
